import React, { useState } from 'react';
import { Film, Theater, Drama, Calendar, Heart, Mail, Phone, MapPin, Play, ExternalLink, X, ChevronLeft, ChevronRight, Images, CreditCard } from 'lucide-react';
import { PaymentForm, CreditCard as SquareCreditCard } from 'react-square-web-payments-sdk';
import { bioData, services, portfolio, upcomingEvents, donationTiers } from '../mock';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { toast } from 'sonner';

const iconMap = {
  Film: Film,
  Theater: Theater,
  Drama: Drama
};

const Home = () => {
  const [ticketForm, setTicketForm] = useState({ event: '', quantity: 1, email: '' });
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('square'); // 'square', 'paypal' or 'cashapp'
  const [galleryModal, setGalleryModal] = useState({ isOpen: false, images: [], currentIndex: 0, title: '' });
  const [donationSuccess, setDonationSuccess] = useState({ show: false, amount: '' });
  const [cardholderName, setCardholderName] = useState({ firstName: '', lastName: '' });

  const openGallery = (images, title, startIndex = 0) => {
    setGalleryModal({ isOpen: true, images, currentIndex: startIndex, title });
  };

  const closeGallery = () => {
    setGalleryModal({ isOpen: false, images: [], currentIndex: 0, title: '' });
  };

  const nextImage = () => {
    setGalleryModal(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const prevImage = () => {
    setGalleryModal(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }));
  };

  const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
  const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
  const CASH_APP_TAG = '$Ejwilsonproductions';

  const handleTicketPurchase = (e) => {
    e.preventDefault();
    toast.success('Ticket purchase request submitted! Check your email for confirmation.');
    setTicketForm({ event: '', quantity: 1, email: '' });
  };

  const handleDonation = (e) => {
    e.preventDefault();
    const amount = donationAmount === 'custom' ? customAmount : donationAmount;
    toast.success(`Thank you for your generous donation of $${amount}!`);
    setDonationAmount('');
    setCustomAmount('');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-gold/20">
        <nav className="container mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold text-gold">
              EJ Wilson Productions
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="nav-link">About</a>
              <a href="#services" className="nav-link">Services</a>
              <a href="#portfolio" className="nav-link">Portfolio</a>
              <a href="#tickets" className="nav-link">Tickets</a>
              <a href="#donate" className="nav-link">Support Us</a>
              <a href="#contact" className="nav-link">Contact</a>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black z-10"></div>
        <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_stage-and-screen/artifacts/zm8blhms_file_00000000677c71fda2916b8930abc963.png"
              alt="EJ Wilson Productions"
              className="mb-6"
              style={{
                maxWidth: '600px',
                width: '100%',
                height: 'auto',
                border: 'none',
                outline: 'none',
                boxShadow: 'none'
              }}
            />
          </div>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
            Where Stories Come to Life Through Film, Stage, and Vision
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="cta-button large" onClick={() => document.getElementById('donate').scrollIntoView({ behavior: 'smooth' })}>
              Support Our Mission
            </Button>
            <Button 
              variant="outline" 
              className="cta-button large border-white text-white hover:bg-white hover:text-black"
              onClick={() => {
                window.open(
                  'https://form.jotform.com/260388819121056',
                  'blank',
                  'scrollbars=yes,toolbar=no,width=700,height=500'
                );
              }}
            >
              <Mail className="w-4 h-4 mr-2" />
              Join Our Mailing List
            </Button>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section id="about" className="py-24 bg-gradient-to-b from-black to-neutral-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1">
              <div className="inline-block mb-4">
                <span className="service-button bg-gold text-black">{bioData.title}</span>
              </div>
              <h2 className="section-heading text-gold mb-6">{bioData.name}</h2>
              <p className="body-large text-white/80 mb-6">
                {bioData.bio}
              </p>
              <div className="space-y-3">
                {bioData.achievements.map((achievement, index) =>
                <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gold rounded-full mt-2"></div>
                    <p className="text-white/70">{achievement}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gold/20 rounded-lg blur-2xl"></div>
                <img
                  src={bioData.image}
                  alt={bioData.name}
                  className="relative rounded-lg shadow-2xl w-full h-auto object-cover" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-neutral-900">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading text-gold mb-4">What We Do</h2>
            <p className="body-large text-white/70 max-w-2xl mx-auto">
              Excellence in every aspect of production and performance
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-black border-gold/20 hover:border-gold/50 transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-gold" />
                    </div>
                    <CardTitle className="card-title-metallic">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-white/70">{service.description}</CardDescription>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-24 bg-black">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading text-gold mb-4">Our Work</h2>
            <p className="body-large text-white/70 max-w-2xl mx-auto">
              A showcase of creative excellence and storytelling mastery
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {portfolio.map((project) =>
            <Card key={project.id} className="bg-neutral-900 border-gold/20 overflow-hidden hover:border-gold/50 transition-all duration-300">
                <CardHeader>
                  {project.videoUrl &&
                <div 
                  className="relative -mx-6 -mt-6 mb-4 bg-black aspect-video flex items-center justify-center group cursor-pointer"
                  onClick={(e) => {
                    const video = e.currentTarget.querySelector('video');
                    const overlay = e.currentTarget.querySelector('.video-overlay');
                    if (video) {
                      if (video.paused) {
                        video.play();
                        video.controls = true;
                        if (overlay) overlay.style.display = 'none';
                      } else {
                        video.pause();
                        video.controls = false;
                        if (overlay) overlay.style.display = 'flex';
                      }
                    }
                  }}
                >
                      <video
                    src={project.videoUrl}
                    className="w-full h-full object-cover"
                    onEnded={(e) => {
                      e.target.controls = false;
                      const overlay = e.target.parentElement.querySelector('.video-overlay');
                      if (overlay) overlay.style.display = 'flex';
                    }}
                    poster="" />

                      <div className="video-overlay absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-black/30 transition-all">
                        <Play className="w-16 h-16 text-gold" />
                      </div>
                    </div>
                }
                  {project.images && project.images.length > 0 &&
                <div 
                  className="relative -mx-6 -mt-6 mb-4 bg-black aspect-video overflow-hidden cursor-pointer group"
                  onClick={() => openGallery(project.images, project.title)}
                >
                      <img
                    src={project.images[0]}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="text-center">
                          <Images className="w-12 h-12 text-gold mx-auto mb-2" />
                          <p className="text-white font-semibold">View {project.images.length} Photos</p>
                        </div>
                      </div>
                    </div>
                }
                  <span className="service-button bg-gold/20 text-gold text-xs">{project.category}</span>
                  <CardTitle className="card-title-metallic">{project.title}</CardTitle>
                  <CardDescription className="text-white/50 text-sm">{project.type}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-white/70 text-sm">{project.description}</p>
                  {project.images && project.images.length > 1 &&
                <p className="text-gold/70 text-xs mt-3">{project.images.length} photos</p>
                }
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Ticket Purchase Section */}
      <section id="tickets" className="py-24 bg-gradient-to-b from-neutral-900 to-black">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="section-heading text-gold mb-4">Upcoming Events</h2>
            <p className="body-large text-white/70 max-w-2xl mx-auto">
              Secure your seats for unforgettable experiences
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {upcomingEvents.map((event) =>
            <Card key={event.id} className="bg-black border-gold/20">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                    <CardTitle className="card-title-metallic mb-2">{event.title}</CardTitle>
                      <CardDescription className="text-white/70">{event.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-white/60">
                      <Calendar className="w-4 h-4 text-gold" />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60">
                      <MapPin className="w-4 h-4 text-gold" />
                      <span>{event.venue}</span>
                    </div>
                    <div className="">
                      {event.price}
                    </div>
                  </div>
                  <Button className="w-full bg-gold text-black hover:bg-gold/90">
                    Purchase Tickets
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
          
          <Card className="bg-neutral-900 border-gold/20 max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="card-title-metallic">Get Your Tickets</CardTitle>
              <CardDescription className="text-white/70">Fill out the form below and we'll contact you with payment details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTicketPurchase} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Select Event</label>
                  <select
                    className="w-full bg-black border border-gold/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                    value={ticketForm.event}
                    onChange={(e) => setTicketForm({ ...ticketForm, event: e.target.value })}
                    required>

                    <option value="">Choose an event</option>
                    {upcomingEvents.map((event) =>
                    <option key={event.id} value={event.title}>{event.title}</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Number of Tickets</label>
                  <Input
                    type="number"
                    min="1"
                    value={ticketForm.quantity}
                    onChange={(e) => setTicketForm({ ...ticketForm, quantity: e.target.value })}
                    className="bg-black border-gold/20 text-white focus:border-gold"
                    required />

                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Email Address</label>
                  <Input
                    type="email"
                    value={ticketForm.email}
                    onChange={(e) => setTicketForm({ ...ticketForm, email: e.target.value })}
                    className="bg-black border-gold/20 text-white focus:border-gold"
                    placeholder="your@email.com"
                    required />

                </div>
                <Button type="submit" className="w-full bg-gold text-black hover:bg-gold/90">
                  Request Tickets
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Donations Section */}
      <section id="donate" className="py-24 bg-black">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-heading text-gold mb-4">Support Our Mission</h2>
            <p className="body-large text-white/70 max-w-2xl mx-auto">
              Your generosity helps us continue creating meaningful art that inspires and transforms
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 mb-12 max-w-6xl mx-auto">
            {donationTiers.map((tier) =>
            <Card key={tier.id} className="bg-neutral-900 border-gold/20 hover:border-gold/50 transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <CardTitle className="card-title-metallic">{tier.name}</CardTitle>
                  <div className="price-metallic text-3xl font-bold mt-2">${tier.amount}</div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-white/70">
                    {tier.benefits.map((benefit, index) =>
                  <li key={index} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-gold rounded-full mt-1.5 flex-shrink-0"></div>
                        <span>{benefit}</span>
                      </li>
                  )}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          <Card className="bg-neutral-900 border-gold/20 max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="card-title-metallic">Make a Donation</CardTitle>
              <CardDescription className="text-white/70">Choose an amount and payment method</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Amount Selection */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-3">Select Amount</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[50, 100, 500, 1000].map((amount) =>
                    <button
                      key={amount}
                      type="button"
                      onClick={() => {
                        setDonationAmount(amount.toString());
                        setCustomAmount('');
                      }}
                      className={`py-3 px-4 rounded-full border-2 transition-all font-bold ${
                      donationAmount === amount.toString() ?
                      'bg-gray-500 text-white border-gray-500 shadow-lg shadow-gray-500/30' :
                      'bg-white/90 text-black border-white/70 hover:bg-gray-300 hover:shadow-lg hover:shadow-white/20 hover:scale-105'}`
                      }>

                        ${amount}
                      </button>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Or enter custom amount</label>
                  <Input
                    type="number"
                    min="1"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setDonationAmount('custom');
                    }}
                    className="bg-white border-gold/20 text-black focus:border-gold placeholder:text-gray-400"
                    placeholder="Enter amount" />

                </div>

                {/* Payment Method Selection */}
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-3">Choose Payment Method</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentMethod('square');
                      }}
                      className={`p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                      paymentMethod === 'square' ?
                      'bg-white border-white shadow-lg shadow-white/30' :
                      'bg-white/90 border-white/70 hover:bg-white hover:shadow-lg hover:shadow-white/20'}`
                      }>
                      <div className="text-center">
                        <CreditCard className="w-6 h-6 mx-auto mb-1 text-black" />
                        <div className="text-xs font-bold text-black">Debit or Credit Card</div>
                        <div className="text-xs text-black/70 font-semibold">Square</div>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentMethod('cashapp');
                      }}
                      className={`p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                      paymentMethod === 'cashapp' ?
                      'bg-white border-white shadow-lg shadow-white/30' :
                      'bg-white/90 border-white/70 hover:bg-white hover:shadow-lg hover:shadow-white/20'}`
                      }>
                      <div className="text-center">
                        <div className="text-sm font-bold text-black">Cash App</div>
                        <div className="text-xs text-black/70 font-semibold">Quick</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Square Card Payment */}
                {paymentMethod === 'square' && (donationAmount || customAmount) && (
                  <div className="bg-black/50 p-4 rounded-lg border border-gold/20">
                    <h3 className="text-gold font-semibold mb-2">Secure Card Payment</h3>
                    <p className="text-white/70 font-semibold mb-4">Complete your donation of ${customAmount || donationAmount}</p>
                    
                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-1">First Name</label>
                        <Input
                          type="text"
                          value={cardholderName.firstName}
                          onChange={(e) => setCardholderName(prev => ({ ...prev, firstName: e.target.value }))}
                          className="bg-white border-gold/20 text-black focus:border-gold placeholder:text-gray-400"
                          placeholder="John"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-1">Last Name</label>
                        <Input
                          type="text"
                          value={cardholderName.lastName}
                          onChange={(e) => setCardholderName(prev => ({ ...prev, lastName: e.target.value }))}
                          className="bg-white border-gold/20 text-black focus:border-gold placeholder:text-gray-400"
                          placeholder="Doe"
                          required
                        />
                      </div>
                    </div>

                    {/* Card Details Label */}
                    <label className="block text-sm font-medium text-white/70 mb-2">Card Details</label>
                    
                    <PaymentForm
                      applicationId={SQUARE_APP_ID}
                      locationId={SQUARE_LOCATION_ID}
                      cardTokenizeResponseReceived={async (token, buyer) => {
                        if (!cardholderName.firstName || !cardholderName.lastName) {
                          toast.error('Please enter your first name and last name.');
                          return;
                        }
                        if (token.status === 'OK') {
                          try {
                            // Send token to backend for payment processing
                            const amount = customAmount || donationAmount;
                            const amountInCents = Math.round(parseFloat(amount) * 100);
                            
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/square`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({
                                source_id: token.token,
                                amount: amountInCents,
                                currency: 'USD',
                                note: `Donation from ${cardholderName.firstName} ${cardholderName.lastName}`
                              }),
                            });
                            
                            const result = await response.json();
                            
                            if (result.success) {
                              setDonationSuccess({ show: true, amount: amount });
                              setDonationAmount('');
                              setCustomAmount('');
                              setCardholderName({ firstName: '', lastName: '' });
                            } else {
                              toast.error(result.error || 'Payment failed. Please try again.');
                            }
                          } catch (error) {
                            console.error('Payment error:', error);
                            toast.error('Payment failed. Please try again.');
                          }
                        } else {
                          toast.error('Card validation failed. Please check your card details.');
                        }
                      }}
                    >
                      <SquareCreditCard buttonProps={{ css: { backgroundColor: '#3b82f6' } }}>
                        Submit
                      </SquareCreditCard>
                    </PaymentForm>
                  </div>
                )}

                {/* Cash App Payment */}
                {paymentMethod === 'cashapp' && (donationAmount || customAmount) && (
                  <div className="bg-black/50 p-4 rounded-lg border border-gold/20">
                    <h3 className="text-gold font-semibold mb-3">Cash App Payment</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-white/70 text-sm mb-2">Send your donation to:</p>
                        <div className="bg-white p-3 rounded-lg border border-gold/30 text-center">
                          <p className="text-black font-bold text-xl">{CASH_APP_TAG}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-white/70 text-sm mb-2">Amount to send:</p>
                        <div className="bg-white p-3 rounded-lg border border-gold/30 text-center">
                          <p className="text-black font-bold text-2xl">${customAmount || donationAmount}</p>
                        </div>
                      </div>
                      <Button
                      type="button"
                      onClick={() => {
                        const amount = customAmount || donationAmount;
                        window.open(`https://cash.app/${CASH_APP_TAG.replace('$', '')}/${amount}`, '_blank');
                        toast.success(`Opening Cash App to send $${amount}`);
                      }}
                      className="w-full bg-yellow-500 text-black font-bold hover:bg-yellow-400 shadow-lg shadow-yellow-500/30">

                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open Cash App
                      </Button>
                      <p className="text-white/50 text-xs text-center">
                        Cash App will open in a new window. Complete the payment there.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-neutral-900 to-black">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="section-heading text-gold mb-4">Get In Touch</h2>
            <p className="body-large text-white/70 max-w-2xl mx-auto">
              Let&apos;s collaborate on your next project or answer any questions
            </p>
          </div>
          <div className="flex justify-center">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold card-title-metallic mb-1">Email</h3>
                  <p className="text-white/70">Ejwilsonproductions@gmail.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold card-title-metallic mb-1">Location</h3>
                  <p className="text-white/70">Orlando, FL</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gold/20 py-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gold mb-4">EJ Wilson Productions</h3>
            <p className="text-white/60 mb-6">Where Stories Come to Life</p>
            <p className="text-white/40 text-sm">&copy; 2025 EJ Wilson Productions. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Photo Gallery Modal */}
      {galleryModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={closeGallery}>
          <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={closeGallery}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full hover:bg-black/80 transition-colors"
            >
              <X className="w-8 h-8 text-white" />
            </button>

            {/* Title */}
            <div className="absolute top-4 left-4 z-10">
              <h3 className="text-gold text-xl font-bold">{galleryModal.title}</h3>
              <p className="text-white/70 text-sm">{galleryModal.currentIndex + 1} of {galleryModal.images.length}</p>
            </div>

            {/* Previous Button */}
            <button
              onClick={prevImage}
              className="absolute left-4 z-10 p-3 bg-black/50 rounded-full hover:bg-gold/80 transition-colors"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>

            {/* Image */}
            <img
              src={galleryModal.images[galleryModal.currentIndex]}
              alt={`${galleryModal.title} - Photo ${galleryModal.currentIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />

            {/* Next Button */}
            <button
              onClick={nextImage}
              className="absolute right-4 z-10 p-3 bg-black/50 rounded-full hover:bg-gold/80 transition-colors"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>

            {/* Thumbnail Navigation */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] p-2">
              {galleryModal.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setGalleryModal(prev => ({ ...prev, currentIndex: idx }))}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === galleryModal.currentIndex ? 'border-gold scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Donation Success Modal */}
      {donationSuccess.show && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setDonationSuccess({ show: false, amount: '' })}>
          <div 
            className="bg-gradient-to-b from-neutral-900 to-black border-2 border-gold rounded-2xl p-8 max-w-md w-full text-center shadow-2xl shadow-gold/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Icon */}
            <div className="w-20 h-20 mx-auto mb-6 bg-gold/20 rounded-full flex items-center justify-center">
              <Heart className="w-10 h-10 text-gold fill-gold" />
            </div>
            
            {/* Thank You Message */}
            <h2 className="text-3xl font-bold text-gold mb-4">Thanks So Much for Your Donation!</h2>
            
            <p className="text-white/80 text-lg mb-2">
              Your generous gift of <span className="text-gold font-bold">${donationSuccess.amount}</span> means the world to us.
            </p>
            
            <p className="text-white/60 mb-8">
              Your support helps bring stories to life and makes a real difference in our creative journey.
            </p>
            
            {/* Close Button */}
            <Button 
              onClick={() => setDonationSuccess({ show: false, amount: '' })}
              className="bg-gold text-black hover:bg-gold/90 font-bold px-8 py-3 text-lg"
            >
              Continue
            </Button>
          </div>
        </div>
      )}
    </div>);

};

export default Home;